import { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { Add, Delete, Edit, DragIndicator } from '@mui/icons-material';
import { IEstacaoKDS, IProcessoPreparoKDS } from '../../interfaces/IKDS';

interface Props {
    processos: IProcessoPreparoKDS[];
    estacoes: IEstacaoKDS[];
    onAdicionar: (dados: Omit<IProcessoPreparoKDS, 'uuid'>) => Promise<void>;
    onAtualizar: (uuid: string, dados: Partial<IProcessoPreparoKDS>) => Promise<void>;
    onRemover: (uuid: string) => Promise<void>;
}

const formInicial = {
    nome: '',
    descricaoExibicao: '',
    estacaoKdsUuid: '',
    tempoPreparoSegundos: 300,
    dependeDe: [] as string[],
    ordem: 0,
    usarFireTime: true,
    aguardaTodosSubitens: false,
};

function detectarCiclo(
    processos: IProcessoPreparoKDS[],
    novoUuid: string,
    dependeDe: string[]
): boolean {
    const visited = new Set<string>();
    const dfs = (atual: string): boolean => {
        if (atual === novoUuid) return true;
        if (visited.has(atual)) return false;
        visited.add(atual);
        const p = processos.find(p => p.uuid === atual);
        return p?.dependeDe.some(dep => dfs(dep)) ?? false;
    };
    return dependeDe.some(dep => dfs(dep));
}

export const ProcessoPreparoForm = ({
    processos,
    estacoes,
    onAdicionar,
    onAtualizar,
    onRemover,
}: Props) => {
    const [dialogoAberto, setDialogoAberto] = useState(false);
    const [editando, setEditando] = useState<IProcessoPreparoKDS | null>(null);
    const [form, setForm] = useState(formInicial);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    const abrirAdicionar = () => {
        setEditando(null);
        setForm({ ...formInicial, ordem: processos.length });
        setErro('');
        setDialogoAberto(true);
    };

    const abrirEditar = (processo: IProcessoPreparoKDS) => {
        setEditando(processo);
        setForm({
            nome: processo.nome,
            descricaoExibicao: processo.descricaoExibicao,
            estacaoKdsUuid: processo.estacaoKdsUuid,
            tempoPreparoSegundos: processo.tempoPreparoSegundos,
            dependeDe: processo.dependeDe,
            ordem: processo.ordem,
            usarFireTime: processo.usarFireTime !== false,
            aguardaTodosSubitens: processo.aguardaTodosSubitens ?? false,
        });
        setErro('');
        setDialogoAberto(true);
    };

    const handleSalvar = async () => {
        if (!form.nome.trim()) { setErro('Nome é obrigatório'); return; }
        if (!form.estacaoKdsUuid) { setErro('Selecione uma estação'); return; }

        // Validação de ciclo
        const processosParaValidar = editando
            ? processos.filter(p => p.uuid !== editando.uuid)
            : processos;
        const uuidSimulado = editando?.uuid ?? '__novo__';
        if (detectarCiclo(
            [...processosParaValidar, { ...form, uuid: uuidSimulado }],
            uuidSimulado,
            form.dependeDe
        )) {
            setErro('Dependência circular detectada');
            return;
        }

        setSalvando(true);
        try {
            if (editando) {
                await onAtualizar(editando.uuid, form);
            } else {
                await onAdicionar(form);
            }
            setDialogoAberto(false);
        } catch {
            setErro('Erro ao salvar processo');
        } finally {
            setSalvando(false);
        }
    };

    const handleRemover = async (uuid: string) => {
        const temDependentes = processos.some(p => p.dependeDe.includes(uuid));
        if (temDependentes) {
            const confirmar = window.confirm(
                'Outros processos dependem deste. Ao remover, as dependências serão removidas. Continuar?'
            );
            if (!confirmar) return;
        }
        await onRemover(uuid);
    };

    const handleDependeDe = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        setForm(f => ({ ...f, dependeDe: typeof value === 'string' ? value.split(',') : value }));
    };

    const processosDisponiveis = editando
        ? processos.filter(p => p.uuid !== editando.uuid)
        : processos;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                    PROCESSOS DE PREPARO
                </Typography>
                <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={abrirAdicionar}
                >
                    Adicionar processo
                </Button>
            </Box>

            {processos.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                    Nenhum processo configurado. O produto será direcionado para a estação da configuração padrão acima.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[...processos].sort((a, b) => a.ordem - b.ordem).map(processo => {
                        const estacao = estacoes.find(e => e.uuid === processo.estacaoKdsUuid);
                        const deps = processo.dependeDe
                            .map(d => processos.find(p => p.uuid === d)?.descricaoExibicao ?? d.slice(-4))
                            .join(', ');
                        return (
                            <Box
                                key={processo.uuid}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    border: '1px solid', borderColor: 'divider',
                                    borderRadius: 1, px: 1.5, py: 1,
                                    borderLeft: `4px solid ${estacao?.cor ?? '#9e9e9e'}`,
                                }}
                            >
                                <DragIndicator sx={{ color: 'text.disabled', fontSize: 18 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap>
                                        {processo.descricaoExibicao}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                                        <Chip
                                            label={estacao?.nome ?? 'Estação desconhecida'}
                                            size="small"
                                            sx={{
                                                background: estacao?.cor ?? '#9e9e9e',
                                                color: 'white',
                                                fontSize: '0.7rem',
                                                height: 18,
                                            }}
                                        />
                                        <Chip
                                            label={`${Math.round(processo.tempoPreparoSegundos / 60)}min`}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontSize: '0.7rem', height: 18 }}
                                        />
                                        {deps && (
                                            <Chip
                                                label={`Depende: ${deps}`}
                                                size="small"
                                                color="warning"
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem', height: 18 }}
                                            />
                                        )}
                                        {processo.usarFireTime === false && (
                                            <Chip
                                                label="Imediato"
                                                size="small"
                                                color="info"
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem', height: 18 }}
                                            />
                                        )}
                                        {processo.aguardaTodosSubitens && (
                                            <Chip
                                                label="Aguarda subitens"
                                                size="small"
                                                color="secondary"
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem', height: 18 }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                                <IconButton size="small" onClick={() => abrirEditar(processo)}>
                                    <Edit fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => handleRemover(processo.uuid)}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Dialog open={dialogoAberto} onClose={() => setDialogoAberto(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editando ? 'Editar Processo' : 'Novo Processo de Preparo'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    <TextField
                        label="Descrição para exibição *"
                        placeholder="Ex: Grill – Hambúrguer Principal"
                        size="small"
                        fullWidth
                        value={form.descricaoExibicao}
                        onChange={e => setForm(f => ({ ...f, descricaoExibicao: e.target.value }))}
                        error={!!erro && !form.descricaoExibicao}
                    />
                    <TextField
                        label="Nome interno (slug) *"
                        placeholder="Ex: grill_hamburguer_principal"
                        size="small"
                        fullWidth
                        value={form.nome}
                        onChange={e => setForm(f => ({ ...f, nome: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                        error={!!erro && !form.nome}
                        helperText="Apenas letras minúsculas, números e _"
                    />
                    <FormControl size="small" fullWidth>
                        <InputLabel>Estação *</InputLabel>
                        <Select
                            value={form.estacaoKdsUuid}
                            label="Estação *"
                            onChange={e => setForm(f => ({ ...f, estacaoKdsUuid: e.target.value }))}
                            error={!!erro && !form.estacaoKdsUuid}
                        >
                            <MenuItem value=""><em>Selecione</em></MenuItem>
                            {estacoes.map(e => (
                                <MenuItem key={e.uuid} value={e.uuid}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: e.cor }} />
                                        {e.nome}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Tempo de preparo (min)"
                        type="number"
                        size="small"
                        fullWidth
                        value={Math.round(form.tempoPreparoSegundos / 60)}
                        onChange={e => setForm(f => ({ ...f, tempoPreparoSegundos: Math.max(1, Number(e.target.value)) * 60 }))}
                        inputProps={{ min: 1, max: 120 }}
                    />
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.usarFireTime}
                                    onChange={e => setForm(f => ({ ...f, usarFireTime: e.target.checked }))}
                                    size="small"
                                />
                            }
                            label="Usar Fire Time"
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                            {form.usarFireTime
                                ? 'Processo aguarda o disparo coordenado com os demais do pedido'
                                : 'Processo aparece na estação imediatamente ao receber o pedido'}
                        </Typography>
                    </Box>
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.aguardaTodosSubitens}
                                    onChange={e => setForm(f => ({ ...f, aguardaTodosSubitens: e.target.checked }))}
                                    size="small"
                                />
                            }
                            label="Aguardar subitens"
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                            {form.aguardaTodosSubitens
                                ? 'Processo só aparece na estação quando todos os subitens do mesmo produto estiverem prontos'
                                : 'Processo segue o fluxo normal de disparo'}
                        </Typography>
                    </Box>
                    {processosDisponiveis.length > 0 && (
                        <>
                            <Divider />
                            <FormControl size="small" fullWidth>
                                <InputLabel>Depende de (processos anteriores)</InputLabel>
                                <Select
                                    multiple
                                    value={form.dependeDe}
                                    onChange={handleDependeDe}
                                    input={<OutlinedInput label="Depende de (processos anteriores)" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(selected as string[]).map(uuid => {
                                                const p = processosDisponiveis.find(p => p.uuid === uuid);
                                                return <Chip key={uuid} label={p?.descricaoExibicao ?? uuid.slice(-4)} size="small" />;
                                            })}
                                        </Box>
                                    )}
                                >
                                    {processosDisponiveis.map(p => (
                                        <MenuItem key={p.uuid} value={p.uuid}>
                                            {p.descricaoExibicao}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </>
                    )}
                    {erro && <Typography color="error" variant="caption">{erro}</Typography>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogoAberto(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSalvar} disabled={salvando}>
                        {salvando ? <CircularProgress size={16} /> : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
